import { useState } from "react";
import { DatePicker, Divider, H6 } from "orcs-design-system";
import moment from "moment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import type { DateSettings } from "src/app/apolloClient";
import { isBeforeDay } from "./isBeforeDay";

type CompareDateRangePickerProps = {
  dateSettings: DateSettings;
  setDateSettings: (changes: Partial<DateSettings>) => void;
  dateFormats: { short: string };
  oldestPersonIndexDateMoment: moment.Moment;
};

const CompareDateRangePicker = ({
  dateSettings,
  setDateSettings,
  dateFormats,
  oldestPersonIndexDateMoment,
}: CompareDateRangePickerProps) => {
  const [focusedInput, setFocusedInput] = useState(null);

  const onDatesChange = (datesChanged: {
    startDate: moment.Moment;
    endDate: moment.Moment;
  }) => {
    const dates = datesChanged;
    if (isBeforeDay(datesChanged.endDate, datesChanged.startDate)) {
      // Switch startDate and endDate if endDate is earlier than startDate
      const { startDate, endDate } = dates;
      dates.startDate = endDate;
      dates.endDate = startDate;
    }
    setDateSettings({
      fromDate: dates.startDate,
      toDate: dates.endDate,
    });
    trackEvent(EVENT_TRACKING.SETTING_CHANGED, {
      setting: "historical_date",
      value: "date_range",
    });
  };

  return (
    <>
      <Divider light />
      <H6 weight="bold">Compare dates</H6>
      {/* The panel is 280px wide with 16px padding either side. Two 123px inputs
          end level with the close button, giving the same gap on the right as on
          the left; the default 134px each overflowed the panel. */}
      <DatePicker
        range
        width="123px"
        startDate={dateSettings.fromDate || moment()}
        startDateId="startDate"
        endDate={dateSettings.toDate || moment()}
        endDateId="endDate"
        startDatePlaceholderText="Start Date"
        endDatePlaceholderText="End Date"
        onDatesChange={onDatesChange}
        focusedInput={focusedInput}
        onFocusChange={setFocusedInput}
        displayFormat={dateFormats.short}
        orientation="horizontal"
        verticalHeight={380}
        horizontalMargin={100}
        daySize={31}
        numberOfMonths={1}
        isOutsideRange={(day) => isBeforeDay(day, oldestPersonIndexDateMoment)}
      />
    </>
  );
};

export default CompareDateRangePicker;
